<template>
  <div class="flex">
    <div class="flex align-items-center" v-if="!editCashlessPay">
      <DeleteConfirmation v-model="showDeleteConfirmation" :isSiteConfig="isSiteConfig" />

      <Button
        id="delete-cashless-pay"
        severity="danger"
        outlined
        :label="translate(`${LOCALE_PREFIX}.actions.delete`)"
        class="mr-3"
        @click.stop="openDeleteConfirmation"
        v-if="hasCashlessPay"
      >
        <template #icon>
          <i class="material-symbols-rounded text-2xl mr-2">delete</i>
        </template>
      </Button>

      <IconButton id="edit-cashless-pay" class="c-icon-button" @click.stop="editCashlessPayMethod">
        <template #icon>
          <i class="material-symbols-rounded text-2xl">edit</i>
        </template>
      </IconButton>
    </div>

    <div class="flex" v-else>
      <Button
        id="cancel-edit-cashless-pay"
        outlined
        severity="secondary"
        class="mr-3"
        :label="translate(`${LOCALE_PREFIX}.actions.cancel`)"
        @click.stop="cancelCashlessPayMethod"
      />

      <Button
        id="save-edit-cashless-pay"
        :label="translate(`${LOCALE_PREFIX}.actions.save`)"
        @click.stop="saveEditCashlessPay"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from '@centric-os/helpers';
import { storeToRefs } from 'pinia';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import { useCashlessPay } from '../composables';
import { useEventBus } from '@vueuse/core';
import DeleteConfirmation from '../delete-confirmation/DeleteConfirmation.vue';
import { ref } from 'vue';
import { SitesLocalePrefix } from '../../../../../enums';
import { useFinancialConfigStore } from '../../../../../routes';
import { FinancialFormIDs } from '../../enums';

const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Ref for show delete confirmation
const showDeleteConfirmation = ref<boolean>(false);

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_CASHLESS_PAYMENT;

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editCashlessPay } = storeToRefs(fConfigStore);

// Set cashless pay composable
const { hasCashlessPay } = useCashlessPay();

// Set form store
const formStore = useFormStore();
const form = formStore.getForm(FinancialFormIDs.CASHLESS_PAY_FORM);

// Set edit cashless pay method
const editCashlessPayMethod = (): void => {
  editCashlessPay.value = true;
};

// Cancel edit cashless pay method
const cancelCashlessPayMethod = (): void => {
  editCashlessPay.value = false;
  form.value?.resetForm();
};

// Set event bus for saving cashless pay
const { emit: saveEdit } = useEventBus<void>(
  `save-cashless-pay-${props.isSiteConfig ? 'site' : 'station'}`,
);

// Save edit cashless pay method
const saveEditCashlessPay = (): void => {
  saveEdit();
};

// Method to show delete confirmation
const openDeleteConfirmation = (): void => {
  showDeleteConfirmation.value = true;
};
</script>
